import type {
  AccommodationStatusResponseDto,
  PlacementDetailsAllocatedJobShift,
  PlacementDetailsWorkRestriction,
} from 'src/store/placementDetailsPageSlice/types';

import type { Site } from '../sites/types';

interface AssociateProfile {
  readonly name: string;
  readonly alias: string;
  readonly location: string;
  readonly employeeId: string;
  readonly managersName: string;
  readonly managersAlias: string;
  readonly shift: string;
  readonly personId: string;
  readonly jobTitle: string;
  readonly totalAccommodatedDays?: number;
}

interface AssociateProfilePlacement {
  readonly placementType: AssociateProfilePlacementType;
  readonly associateFullName?: string;
  readonly alias: string;
  readonly accommodationStatus: string;
  readonly accommodationId: number;
  readonly externalCaseId: string;
  readonly accommodationType: string;
  readonly name: string;
  readonly nextStatuses?: AccommodationStatusResponseDto[];
  readonly allocatedJobShifts: PlacementDetailsAllocatedJobShift[];
  readonly additionalTags: string[];
  readonly injury: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly timeInStatus: string;
  readonly transferAt: string;
  readonly transferredFrom: string;
  readonly newestJmrFileName: string;
  readonly modifiedShiftLength: number;
  readonly managerAlias: string;
  readonly fc: string;
  readonly workRestrictions: PlacementDetailsWorkRestriction[];
  readonly closedAt: string;
  readonly site: Site;
}

interface AssociateProfilePlacementsResponse {
  readonly pending: AssociateProfilePlacement[];
  readonly active: AssociateProfilePlacement[];
  readonly transferredIn: AssociateProfilePlacement[];
}

interface AssociateProfileRestriction {
  readonly placementType: AssociateProfilePlacementType;
  readonly accommodationId: number;
  readonly accommodationType: string;
  readonly externalCaseId: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly taskName: string;
  readonly endAt: string;
  readonly hoursRestricted: string | null | undefined;
  readonly bodyLocation: string;
}

interface AssociateProfileLimitation {
  readonly placementType: AssociateProfilePlacementType;
  readonly accommodationId: number;
  readonly accommodationType: string;
  readonly externalCaseId: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly taskName: string;
  readonly freeFormText: string;
  readonly duration: number;
  readonly frequency: number;
}

interface AssociateProfileRestrictionsResponse {
  readonly pending: AssociateProfileRestriction[];
  readonly active: AssociateProfileRestriction[];
  readonly transferredIn: AssociateProfileRestriction[];
}

interface AssociateProfileLimitationsResponse {
  readonly pending: AssociateProfileLimitation[];
  readonly active: AssociateProfileLimitation[];
  readonly transferredIn: AssociateProfileLimitation[];
}

export enum AssociateProfilePlacementType {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  TRANSFERRED = 'TRANSFERRED',
  CLOSED = 'CLOSED',
}

interface ModifiedDutySiteSummary {
  readonly siteId: number;
  readonly site: string;
  readonly modifiedDutyDays: number;
  readonly unknownDutyDays: number;
  readonly processPathDays: number;
}

interface ModifiedDutyCountrySummary {
  readonly countryId: number;
  readonly countryName: string;
  readonly modifiedDutyDays: number;
  readonly unknownDutyDays: number;
  readonly processPathDays: number;
  readonly sites: ModifiedDutySiteSummary[];
}

interface ModifiedDutyTotalSummary {
  readonly modifiedDutyDays: number;
  readonly unknownDutyDays: number;
  readonly processPathDays: number;
  readonly modifiedDutyCountdownDate: string;
  readonly unknownDutyCountdownDate: string;
}

interface ModifiedDutySummaryResponse {
  readonly total: ModifiedDutyTotalSummary;
  readonly countrySummary: ModifiedDutyCountrySummary[];
}

export type {
  AssociateProfile,
  AssociateProfilePlacement,
  AssociateProfilePlacementsResponse,
  AssociateProfileRestriction,
  AssociateProfileLimitation,
  AssociateProfileRestrictionsResponse,
  AssociateProfileLimitationsResponse,
  ModifiedDutyCountrySummary,
  ModifiedDutySiteSummary,
  ModifiedDutySummaryResponse,
  ModifiedDutyTotalSummary,
};
